<template>
  <div
    tabindex="1"
    class="select-container"
    @focus="showOptions"
    @blur="handleBlur"
  >
    <div
      ref="mainContainer"
      :class="[disabled ? 'bg-accent-disabled-multiselect' : 'bg-accent-multiselect']"
    >
      <div class="w-11/12 flex flex-row items-center">
        <span
          class="w-11/12"
          :class="[(selected && selected.text) ? 'text-black' : 'text-grey-001']"
        >{{ (selected && selected.text) ? selected.text : placeholder }}</span>
      </div>
      <div class="w-1/12 flex flex-row items-center justify-end">
        <vs-icon
          :icon="isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down'"
          :color="disabled ? '#AAAAAA': '#493493'"
        />
      </div>
    </div>
    <div
      v-show="isOptionShown"
      class="bg-option-container"
    >
      <div
        v-if="!isAddNew"
        class="bg-inner-option-container"
      >
        <div
          v-if="!tempSelected.length"
          class="flex flex-row items-center justify-center"
        >
          <span class="empty-option">Nothing can be shown</span>
        </div>
        <ul v-else>
          <li
            v-for="(item, index) of tempSelected"
            class="my-1"
            :key="index"
            :class="{'active': isOptionSelected(item)}"
            @click.stop="selectValue(item)"
          >
            <div class="flex flex-row">
              <div class="w-full flex flex-row items-center">
                <span>{{ item.text }}</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div
        v-else
        class="bg-inner-option-container"
      >
        <div class="bg-inner-add-option-container">
          <div class="flex flex-row items-center justify-center">
            <div class="w-11/12">
              <span class="text-primary font-semibold">Add New</span>
            </div>
            <div class="w-1/12 flex flex-row items-center justify-end">
              <div
                class="p-1 flex flex-col justify-center rounded-md cursor-pointer bg-secondary-accent"
                @click="isAddNew = false"
              >
                <vs-icon
                  icon="close"
                  color="#FF7F57"
                  size="12px"
                />
              </div>
            </div>
          </div>
          <vs-input
            v-model="itemName"
            placeholder="Sample new Name"
            class="mt-5"
          />
        </div>
      </div>
      <div class="bg-inner-footer-container">
        <vs-button
          type="filled"
          color="secondary"
          class="w-32 rounded-lg font-semibold"
          :class="[isAddNew ? 'w-32' : 'w-40']"
          @click="isAddNew ? addNewItem() : (isAddNew = true)"
        >
          {{ isAddNew ? 'Add' : 'Add New Item' }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: null,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => 'Select option'
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    isObjectModel: {
      type: Boolean,
      default: () => false
    },
    isValid: {
      type: Boolean,
      default: () => true
    },
    /**
     * Array of available options: Objects.
     * Objects must consist of `value` and `text`
     * example: `{ value: 'something', text: 'something' }`
     */
    options: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOptionShown: false,
      isAddNew: false,
      selected: this.isObjectModel
        ? this.value
        : {
          value: this.value,
          text: this.getText()
        },
      itemName: null,
      tempSelected: []
    }
  },
  watch: {
    value: {
      handler(val) {
        this.selected = this.isObjectModel
          ? val
          : {
            value: val,
            text: this.getText()
          }
      }
    },
    isValid: {
      handler(val) {
        if (!this.disabled) {
          if (val) {
            this.$refs.mainContainer.classList.remove('bg-accent-danger-multiselect')
            this.$refs.mainContainer.classList.add('bg-accent-multiselect')
            return
          }

          this.$refs.mainContainer.classList.remove('bg-accent-multiselect')
          this.$refs.mainContainer.classList.add('bg-accent-danger-multiselect')
          return
        }
        this.$refs.mainContainer.classList.add('bg-accent-disabled-multiselect')
      }
    }
  },
  methods: {
    showOptions() {
      if (!this.disabled && !this.isOptionShown) {
        /**
         * if `options` is not mapped,
         * `options` will be following `tempSelected`
         * when `tempSelected` react to data change
         */
        this.tempSelected = this.options.map(item => item)
        this.isOptionShown = true

        this.$el.focus()
      }
    },
    closeOptions() {
      if (!this.disabled && this.isOptionShown) {
        this.isOptionShown = false

        this.$el.blur()
      }
    },
    handleBlur(ev) {
      if (!ev.currentTarget.contains(ev.relatedTarget)) {
        this.closeOptions()
      }
    },
    selectValue(item) {
      this.selected = item
      this.$emit('input', this.isObjectModel
        ? this.selected
        : this.selected.value
      )
      this.$emit('change')

      this.closeOptions()
    },
    getText() {
      if (this.value) {
        return this.options.length
          ? this.options.find(item => item.value === this.value).text
          : null
      }
      return null
    },
    addNewItem() {
      this.tempSelected.push({ value: this.itemName, text: this.itemName })
      this.$emit('update', this.tempSelected)
      this.$emit('postUpdate', this.itemName)

      this.itemName = null
      this.isAddNew = false
    },
    isOptionSelected(item) {
      if (this.selected) {
        return this.selected.value === item.value
      }

      return this.selected
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  @apply p-3 rounded-lg cursor-pointer;

  color: #3D3D3D;

  &:hover {
    @apply font-semibold;

    background-color: #F1F1FD;
    color: #6546C3;
  }
}

.select-container {
  @apply w-full relative;
}

.bg-accent-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-accent-disabled-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-not-allowed;

  height: calc(1rem * 10 / 3);
  background-color: #ECEBED;
}

.bg-accent-danger-multiselect {
  @apply p-3 w-full flex flex-row border-2 border-danger rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-option-container {
  @apply mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg z-10;

  max-height: 20rem;
}

.bg-inner-option-container {
  @apply p-3 mt-3 flex flex-col overflow-auto;

  height: 15rem;
}

.bg-inner-add-option-container {
  @apply w-full flex flex-col;
}

.bg-inner-footer-container {
  @apply p-3 flex flex-row items-center justify-end shadow-inner;
}

.empty-option {
  @apply italic text-center;

  color: #3D3D3D;
}

.active {
  @apply font-semibold;

  background-color: #F1F1FD;
  color: #6546C3;
}
</style>
